/* Built In Imports */
import { useState } from 'react';

/* External Imports */
import { ChevronLeftIcon, ChevronRightIcon } from '@chakra-ui/icons';
import { Box, Button, Icon, Image, Text } from '@chakra-ui/react';
import { render } from 'datocms-structured-text-to-html-string';
import { useMediaQuery } from 'react-responsive';
import Slider from 'react-slick';

/* Internal Imports */
/* Components */
import StructuredTextParser from '@moms/Utility/StructuredTextParser';
import LandingVoiceSt from '@moms/styleData/LandingVoiceSt';

/* Styles */
/* Services */

/**
 *
 * @param {Object} sectionContent
 * @param {String} region
 * @param {String} lang
 * @returns
 */

const MainLayout = ({ region, lang, sectionContent }) => {
  return (
    <Box
      maxW={{ base: '900px', lg: '900px', xl: '1070px', '2xl': '1100px' }}
      pt={{ base: '80px', lg: '144px' }}
      pb={{ base: '138px', lg: '144px' }}
      px={{ base: '18px', md: '50px', xl: '76px' }}
      mx="auto"
      borderInline="1px solid #ffffff33"
      display="flex"
      flexDirection="column"
      gap={{ base: '40px', md: '64px' }}
    >
      {/* headings and subheadings */}
      <Headings region={region} lang={lang} sectionContent={sectionContent} />
      {/* slider layout */}
      <SliderLayout
        region={region}
        lang={lang}
        sectionContent={sectionContent}
      />
    </Box>
  );
};

const Headings = ({ region, lang, sectionContent }) => {
  const landingVoiceData =
    sectionContent.find(
      item => item._modelApiKey === 'testimonials_group_v2'
    ) || sectionContent?.[0];

  return (
    <Box display="flex" flexDirection="column" gap="8px">
      <Text {...LandingVoiceSt[lang]?.titleText}>
        {landingVoiceData?.title[0]?.titleText}
      </Text>
      <Text {...LandingVoiceSt[lang]?.titleSubtext}>
        {landingVoiceData?.title[0]?.titleSubtext}
      </Text>
      <Text {...LandingVoiceSt[lang]?.additionalTextLine}>
        {landingVoiceData?.title[0]?.additionalTextLine}
      </Text>
    </Box>
  );
};

const SliderLayout = ({ region, lang, sectionContent }) => {
  const landingVoiceData =
    sectionContent.find(
      item => item._modelApiKey === 'testimonials_group_v2'
    ) || sectionContent?.[0];

  const isMobile = useMediaQuery({ maxWidth: 1023 });

  const [currentSlide, setCurrentSlide] = useState(0);
  const totalSlides = Math.ceil(landingVoiceData?.testimonials?.length / 1);

  const CustomPrevArrow = ({ onClick }) => (
    <Box
      {...LandingVoiceSt[lang]?.sliderIcon}
      left={{ base: '-8%', lg: '-14.5%', xl: '-16%', '2xl': '-18%' }}
      cursor={currentSlide === 0 ? 'not-allowed' : 'pointer'}
      opacity={currentSlide === 0 ? 0.5 : 1}
      onClick={currentSlide === 0 ? undefined : onClick}
      display={{ base: 'none', md: 'block' }}
    >
      <Icon as={ChevronLeftIcon} boxSize={12} color="#fff" />
    </Box>
  );

  const CustomNextArrow = ({ onClick }) => (
    <Box
      {...LandingVoiceSt[lang]?.sliderIcon}
      right={{ base: '-8%', lg: '-14.5%', xl: '-16%', '2xl': '-18%' }}
      cursor={currentSlide === totalSlides - 1 ? 'not-allowed' : 'pointer'}
      opacity={currentSlide === totalSlides - 1 ? 0.5 : 1}
      onClick={currentSlide === totalSlides - 1 ? undefined : onClick}
      display={{ base: 'none', md: 'block' }}
    >
      <Icon as={ChevronRightIcon} boxSize={12} color="#fff" />
    </Box>
  );
  const settings = {
    dots: isMobile ? true : false,
    speed: 500,
    slidesToShow: 1,
    slidesToScroll: 1,
    arrows: true,
    infinite: false,
    cssEase: 'linear',
    beforeChange: (oldIndex, newIndex) => setCurrentSlide(newIndex / 1),
    prevArrow: <CustomPrevArrow />,
    nextArrow: <CustomNextArrow />,

    customPaging: i => (
      <Box
        width={{ base: '11.39px' }}
        height={{ base: '11.39px' }}
        borderRadius="50%"
        mt={{ base: '1rem' }}
        bgColor=" #FFFFFF33"
        transition="background-color 0.3s, opacity 0.3s"
        css={{
          '.slick-active &': {
            backgroundColor: '#EC1C2D',
            opacity: '1',
          },
        }}
        _hover={{ bgColor: '#08152D' }}
      />
    ),
  };

  return (
    <Box position="relative">
      <Slider {...settings}>
        {landingVoiceData?.testimonials?.map((slide, index) => {
          return (
            <Box
              key={index}
              display="flex !important"
              flexDirection={{ base: 'column', lg: 'row' }}
              gap="24px"
              p="10px"
            >
              {/* Image */}
              <Image
                src={slide?.photo?.url}
                alt={slide?.photo?.alt}
                title={slide?.photo?.title}
                w={isMobile ? '208px' : '306px'}
                h={isMobile ? '215px' : '334px'}
                objectFit="cover"
                objectPosition="top"
                mx={{ base: 'auto', lg: '0' }}
              />

              {/* Text Content */}
              <Box
                display="flex"
                flexDirection="column"
                justifyContent="flex-end"
                maxW={{ lg: '600px' }}
                gap={{ base: '10px', lg: '0' }}
              >
                <Box
                  display="flex"
                  flexDir="column"
                  maxW={{ base: '100%', lg: '540px' }}
                >
                  <Text {...LandingVoiceSt[lang]?.userNameText}>
                    {slide?.userName}
                  </Text>
                  <StructuredTextParser
                    {...LandingVoiceSt[lang]?.testimonialTitle}
                    str={render(slide?.testimonialTitle)}
                    mt="8px"
                  />
                </Box>
                <StructuredTextParser
                  {...LandingVoiceSt[lang]?.testimonialText}
                  str={render(slide?.testimonialText)}
                  mt="40px"
                />
                <Button
                  as="a"
                  href={slide?.details?.url}
                  mt="16px"
                  width="fit-content"
                  minH="48px"
                  height="auto"
                  bgColor="transparent"
                  border="none"
                  borderBottom="1px solid rgba(236, 28, 45, 1)"
                  _hover={{ borderBottom: '1px solid #636EFE' }}
                  _focus={{ borderBottom: '1px solid #636EFE' }}
                  _active={{ borderBottom: '1px solid #636EFE' }}
                  _focusVisible={{
                    boxShadow: 'none',
                    borderBottom: '1px solid #636EFE',
                  }}
                  borderRadius="0"
                  py="10px"
                  ps="8px"
                  pe="0"
                  display="flex"
                  justifyContent="space-between"
                  gap="8px"
                  mx={{ base: 'auto', lg: '0' }}
                  color="#fff"
                  cursor="pointer"
                  whiteSpace="break-spaces"
                >
                  <StructuredTextParser
                    {...LandingVoiceSt[lang]?.details}
                    str={render(slide?.details)}
                    mt="0"
                  />
                  <ChevronRightIcon width="32px" height="32px" padding="3px" />
                </Button>
              </Box>
              <style jsx global>{`
                .slick-dots li button:before {
                  color: rgba(255, 255, 255, 0.2) !important;
                  font-size: 12px !important;
                }
                .slick-dots li.slick-active button:before {
                  color: #ec1c2d !important;
                }
                .slick-slider .slick-dots {
                  bottom: -75px !important;
                  text-align: center;
                }
                .slick-dots li {
                  margin: 0 4px !important;
                }
                slick-slide:focus {
                  outline: none !important;
                  border: none !important;
                }
                .slick-slide div:focus {
                  outline: none !important;
                }
                .slick-slide img {
                  outline: none !important;
                }
              `}</style>
            </Box>
          );
        })}
      </Slider>
    </Box>
  );
};

export const LandingVoice = ({ sectionContent, region, lang }) => {
  return (
    <Box
      borderBlock="1px solid #ffffff33"
      color="#fff"
      background="linear-gradient(82.08deg, #12141C 36%, #DC2C0C 400%)"
    >
      <MainLayout region={region} lang={lang} sectionContent={sectionContent} />
    </Box>
  );
};
